import { z } from 'zod';

/** Coded defaults for every list endpoint. */
export const DEFAULT_PAGE = 1;
export const DEFAULT_PAGE_SIZE = 20;

/**
 * The hard ceiling on a single page. A request above it is REJECTED rather than
 * clamped: silent clamping hides a client bug and, on a large table, invites an
 * accidental full read (N7).
 */
export const MAX_PAGE_SIZE = 100;

export const SORT_ORDERS = ['asc', 'desc'] as const;
export const SortOrderSchema = z.enum(SORT_ORDERS);
export type SortOrder = (typeof SORT_ORDERS)[number];

/**
 * The pagination/sort half of every list query. Per-resource filters are
 * whitelisted separately through the FilterSpec layer (N7) — this schema never
 * grows a passthrough.
 *
 * Numbers are coerced because they arrive as query strings; booleans are NOT
 * (N7 forbids `z.coerce.boolean()`, which treats `'false'` as true).
 */
export const PaginationQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(DEFAULT_PAGE),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    sort: z.string().min(1).optional(),
    order: SortOrderSchema.default('desc'),
  })
  .strict();

export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

/** The meta block returned alongside every paginated payload. */
export const PaginationMetaSchema = z.object({
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0),
  hasNext: z.boolean(),
  hasPrevious: z.boolean(),
});

export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;

/**
 * Wraps an item schema in the standard paginated payload shape.
 *
 * @example
 * const MembersPageSchema = makePaginatedSchema(MemberSummarySchema);
 */
export function makePaginatedSchema<TItem extends z.ZodType>(item: TItem) {
  return z.object({
    items: z.array(item),
    meta: PaginationMetaSchema,
  });
}

export type Paginated<TItem> = {
  items: TItem[];
  meta: PaginationMeta;
};

/** Builds the meta block from a total and the resolved query. */
export function buildPaginationMeta(total: number, query: PaginationQuery): PaginationMeta {
  const totalPages = Math.ceil(total / query.pageSize);
  return {
    page: query.page,
    pageSize: query.pageSize,
    total,
    totalPages,
    hasNext: query.page < totalPages,
    hasPrevious: query.page > 1,
  };
}
